// Implements: F-IDN-015, F-UIX-009, F-UIX-007
/**
 * The own sign-in attempts (v3 `acctAccess`): date, result (badge with text, never colour alone),
 * device with its address and place, and why a failure failed in words. A success has no reason
 * and shows "—". `busy` marks rows that answer a previous filter while the new one loads: dimmed
 * and `aria-busy`, so they never read as the current result. Pure presentation.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import type { AccessReason, AccessRecordSummary } from "../../../types/api";
import { Badge } from "../../../shared/ui/atoms/Badge";
import type { BadgeTone } from "../../../shared/ui/atoms/Badge";
import { formatLocation } from "../../../shared/ui/locationText";
import { dateText } from "../model/dates";
import styles from "./AccessLogTable.module.css";

const OUTCOME: Readonly<
  Record<
    AccessRecordSummary["outcome"],
    { readonly tone: BadgeTone; readonly key: "account.access.success" | "account.access.failure" | "account.access.blocked" }
  >
> = {
  success: { tone: "success", key: "account.access.success" },
  failure: { tone: "warning", key: "account.access.failure" },
  blocked: { tone: "danger", key: "account.access.blocked" },
};

const REASON: Readonly<Record<AccessReason, Extract<TranslationKey, `account.reason.${string}`>>> = {
  wrong_password: "account.reason.wrongPassword",
  account_inactive: "account.reason.accountInactive",
  second_factor_missing: "account.reason.secondFactorMissing",
  second_factor_invalid: "account.reason.secondFactorInvalid",
  second_factor_reused: "account.reason.secondFactorReused",
  organizations_suspended: "account.reason.organizationsSuspended",
};

export const NO_REASON = "—";

export interface AccessLogTableProps {
  readonly records: readonly AccessRecordSummary[];
  /** The rows answer a previous filter while the current one loads. */
  readonly busy?: boolean;
}

export function AccessLogTable({ records, busy = false }: AccessLogTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.wrap}>
      <table
        className={busy ? [styles.table, styles.busy].join(" ") : styles.table}
        aria-label={t("account.access.title")}
        aria-busy={busy}
      >
        <thead>
          <tr>
            <th scope="col">{t("account.access.date")}</th>
            <th scope="col">{t("account.access.result")}</th>
            <th scope="col">{t("account.access.origin")}</th>
            <th scope="col">{t("account.access.reason")}</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => {
            const outcome = OUTCOME[record.outcome];
            const address = record.client_ip ?? t("account.access.unknownIp");
            const place =
              record.location === null
                ? null
                : formatLocation(record.location, t("account.location.unknown"));
            const reason = record.reason === null ? null : REASON[record.reason];
            return (
              <tr key={`${String(index)}-${record.occurred_at}`}>
                <td className={styles.meta}>
                  <time dateTime={record.occurred_at}>{dateText(record.occurred_at, "moment", NO_REASON)}</time>
                </td>
                <td>
                  <Badge tone={outcome.tone}>{t(outcome.key)}</Badge>
                </td>
                <td>
                  <span className={styles.strong}>{record.device_label ?? t("account.access.unknownDevice")}</span>
                  <div className={styles.meta}>{place === null ? address : `${address} · ${place}`}</div>
                </td>
                <td>{reason === null ? <span className={styles.meta}>{NO_REASON}</span> : t(reason)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
